import { useSnackbar } from 'notistack';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useService } from 'shared/providers';
import { DownloadPdfProps, GetQuotesProps, GetQuotesResponse } from 'shared/services/quote-service';
import { QuoteDocument, QuoteDocumentWithoutId } from 'shared/types/quotes';

export function useCreateQuote() {
  const { quoteService } = useService();
  const queryClient = useQueryClient();
  const mutation = useMutation<QuoteDocument, Error, QuoteDocumentWithoutId>(
    request => quoteService.createQuote(request),
    {
      onSuccess: data => {
        queryClient.invalidateQueries('quotes');
      },
      onError: () => {},
    }
  );

  return mutation;
}

export function useEditQuote() {
  const { quoteService } = useService();
  const queryClient = useQueryClient();
  const mutation = useMutation<{ operationId: string }, Error, QuoteDocument>(
    request => quoteService.updateQuote(request),
    {
      onSuccess: data => {
        queryClient.invalidateQueries('quote');
        queryClient.invalidateQueries('quotes');
      },
      onError: () => {},
    }
  );

  return mutation;
}

export function useQueryQuotes(props: GetQuotesProps) {
  const { quoteService } = useService();
  const { data, isLoading, isFetching } = useQuery<GetQuotesResponse, Error>(
    ['quotes', props],
    () => quoteService.getQuotes(props),
    {
      keepPreviousData: true,
    }
  );
  return { quotes: data, isLoading, isFetching } as const;
}

export function useGetQuote(id: string) {
  const { quoteService } = useService();
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading, isFetching } = useQuery<QuoteDocument, Error>(
    ['quote', id],
    () => quoteService.getQuote(id),
    {
      onError: () => {
        enqueueSnackbar(`Quote ${id} does not exist..`, { variant: 'error' });
      },
    }
  );
  return { data, isLoading, isFetching } as const;
}

export function usePdfExport<T>() {
  const { quoteService } = useService();
  const { enqueueSnackbar } = useSnackbar();
  const mutation = useMutation<void, Error, DownloadPdfProps<T>>(request => quoteService.downloadPdf(request), {
    onSuccess: () => {
      enqueueSnackbar('Quote was exported', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Quote was not exported', { variant: 'error' });
    },
  });

  return mutation;
}

export function useDeleteQuote() {
  const { quoteService } = useService();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const mutation = useMutation<{ operationId: string }, Error, QuoteDocument>(
    request => quoteService.updateQuote(request),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('quote');
        queryClient.invalidateQueries('quotes');
        enqueueSnackbar('Successfully deleted quote ', { variant: 'success' });
      },
      onError: () => {
        enqueueSnackbar('Failed to delete quote', { variant: 'error' });
      },
    }
  );

  return mutation;
}
